
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Shield, Zap, Cpu, Globe, Heart, CheckCircle, Star, Award } from 'lucide-react';

const HomeSeerSection = () => {
  const advantages = [
  {
    icon: <Cpu className="w-8 h-8" data-id="uxei6i3bl" data-path="src/components/HomeSeerSection.tsx" />,
    title: 'Control Personalizado',
    description: 'Crea automatizaciones poderosas sin necesidad de programación o código complejo.',
    highlight: 'Sin código necesario'
  },
  {
    icon: <Shield className="w-8 h-8" data-id="6v7zvye9l" data-path="src/components/HomeSeerSection.tsx" />,
    title: 'Gestión Local',
    description: 'Mayor velocidad, fiabilidad y privacidad al no depender de servicios en la nube.',
    highlight: 'Privacidad total'
  },
  {
    icon: <Globe className="w-8 h-8" data-id="e3r1i88fc" data-path="src/components/HomeSeerSection.tsx" />,
    title: 'Amplia Compatibilidad',
    description: 'Conecta sin esfuerzo cualquier dispositivo: Zigbee, Z-Wave, Hue, Sonos, Google, Alexa.',
    highlight: '500+ dispositivos'
  },
  {
    icon: <Zap className="w-8 h-8" data-id="wqkgozj1a" data-path="src/components/HomeSeerSection.tsx" />,
    title: 'Flexibilidad Total',
    description: 'Soluciones escalables para cada necesidad y presupuesto, desde DIY hasta instalaciones profesionales.',
    highlight: 'Cualquier presupuesto'
  }];


  const stats = [
  { number: '25', label: 'Años de Experiencia', icon: <Award className="w-6 h-6" data-id="ycw1w47vr" data-path="src/components/HomeSeerSection.tsx" /> },
  { number: '500K+', label: 'Dispositivos Compatibles', icon: <Globe className="w-6 h-6" data-id="9dbmqxx52" data-path="src/components/HomeSeerSection.tsx" /> },
  { number: '99.9%', label: 'Tiempo de Funcionamiento', icon: <Shield className="w-6 h-6" data-id="w96ut6amf" data-path="src/components/HomeSeerSection.tsx" /> },
  { number: '4.8/5', label: 'Satisfacción del Cliente', icon: <Star className="w-6 h-6" data-id="ztafglnyt" data-path="src/components/HomeSeerSection.tsx" /> }];


  const comparisons = [
  { feature: 'Control Local (Sin Internet)', homeseer: true, others: false },
  { feature: 'Privacidad de Datos', homeseer: true, others: false },
  { feature: 'Personalización Avanzada', homeseer: true, others: false },
  { feature: 'Compatibilidad Universal', homeseer: true, others: false },
  { feature: 'Soporte Profesional', homeseer: true, others: false },
  { feature: 'Sin Suscripciones', homeseer: true, others: false }];


  return (
    <section id="homeseer" className="py-20 bg-white" data-id="4kus7wubl" data-path="src/components/HomeSeerSection.tsx">
      <div className="container mx-auto px-4" data-id="3d9jewon9" data-path="src/components/HomeSeerSection.tsx">
        {/* Header */}
        <div className="text-center mb-16" data-id="eeg1w1zcz" data-path="src/components/HomeSeerSection.tsx">
          <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 text-sm font-medium mb-4" data-id="lvkqszt4r" data-path="src/components/HomeSeerSection.tsx">
            🏆 Solución Líder Mundial
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="rvofjvqsw" data-path="src/components/HomeSeerSection.tsx">
            Por Qué <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="ow6w68yf3" data-path="src/components/HomeSeerSection.tsx">HomeSeer</span> es la
            <br data-id="mmlgab94j" data-path="src/components/HomeSeerSection.tsx" />Elección Inteligente
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="mca0y5dxw" data-path="src/components/HomeSeerSection.tsx">
            Con 25 años de experiencia y más de 500,000 dispositivos compatibles, HomeSeer es la plataforma 
            de automatización más confiable y flexible del mercado.
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid md:grid-cols-4 gap-6 mb-16" data-id="jc9h3a80o" data-path="src/components/HomeSeerSection.tsx">
          {stats.map((stat, index) =>
          <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300" data-id="cvflh1que" data-path="src/components/HomeSeerSection.tsx">
              <CardContent className="p-6" data-id="sqdb6vyca" data-path="src/components/HomeSeerSection.tsx">
                <div className="text-blue-600 mb-3 flex justify-center" data-id="05nls97qg" data-path="src/components/HomeSeerSection.tsx">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2" data-id="al7iq87iy" data-path="src/components/HomeSeerSection.tsx">{stat.number}</div>
                <div className="text-sm text-gray-600" data-id="82vigy2p3" data-path="src/components/HomeSeerSection.tsx">{stat.label}</div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Advantages */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16" data-id="w1vjyuj69" data-path="src/components/HomeSeerSection.tsx">
          {advantages.map((advantage, index) =>
          <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden" data-id="gwtb8xs4j" data-path="src/components/HomeSeerSection.tsx">
              <CardContent className="p-8" data-id="zge6f6yci" data-path="src/components/HomeSeerSection.tsx">
                <div className="flex items-start space-x-4" data-id="hv9727b93" data-path="src/components/HomeSeerSection.tsx">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-3 rounded-lg group-hover:scale-110 transition-transform duration-300" data-id="miz7214ww" data-path="src/components/HomeSeerSection.tsx">
                    {advantage.icon}
                  </div>
                  <div className="flex-1" data-id="lcpzipd2e" data-path="src/components/HomeSeerSection.tsx">
                    <div className="flex items-center justify-between mb-3" data-id="6yd3da40k" data-path="src/components/HomeSeerSection.tsx">
                      <h3 className="text-xl font-bold text-gray-900" data-id="pc5ojhpxi" data-path="src/components/HomeSeerSection.tsx">{advantage.title}</h3>
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700" data-id="4cmuvnmsi" data-path="src/components/HomeSeerSection.tsx">
                        {advantage.highlight}
                      </Badge>
                    </div>
                    <p className="text-gray-600 leading-relaxed" data-id="yulm5oh97" data-path="src/components/HomeSeerSection.tsx">{advantage.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Comparison Table */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-16" data-id="t2xuz28tc" data-path="src/components/HomeSeerSection.tsx">
          <div className="text-center mb-8" data-id="kbie0361j" data-path="src/components/HomeSeerSection.tsx">
            <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="7l1ut75xd" data-path="src/components/HomeSeerSection.tsx">
              HomeSeer vs. Otras Soluciones
            </h3>
            <p className="text-gray-600" data-id="ojgpskh3r" data-path="src/components/HomeSeerSection.tsx">
              Descubre por qué HomeSeer es la mejor opción para tu hogar inteligente
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg" data-id="g2orv7pq7" data-path="src/components/HomeSeerSection.tsx">
            <div className="grid grid-cols-3 gap-4 mb-4" data-id="l0zvdp1x4" data-path="src/components/HomeSeerSection.tsx">
              <div className="font-semibold text-gray-900" data-id="rsgk0zlpg" data-path="src/components/HomeSeerSection.tsx">Características</div>
              <div className="text-center" data-id="7hif156e8" data-path="src/components/HomeSeerSection.tsx">
                <div className="font-bold text-blue-600 text-lg" data-id="jttcav8iz" data-path="src/components/HomeSeerSection.tsx">HomeSeer</div>
                <div className="text-sm text-gray-600" data-id="6zb1ulnyl" data-path="src/components/HomeSeerSection.tsx">Nuestra Solución</div>
              </div>
              <div className="text-center" data-id="5pl510w1n" data-path="src/components/HomeSeerSection.tsx">
                <div className="font-bold text-gray-500 text-lg" data-id="xy8fyfjhf" data-path="src/components/HomeSeerSection.tsx">Competencia</div>
                <div className="text-sm text-gray-600" data-id="ews2e0rq5" data-path="src/components/HomeSeerSection.tsx">Otras Marcas</div>
              </div>
            </div>

            {comparisons.map((comparison, index) =>
            <div key={index} className="grid grid-cols-3 gap-4 py-3 border-t border-gray-100" data-id="hurdckkcf" data-path="src/components/HomeSeerSection.tsx">
                <div className="text-gray-700" data-id="0qtlf4wvv" data-path="src/components/HomeSeerSection.tsx">{comparison.feature}</div>
                <div className="text-center" data-id="0zloxskuk" data-path="src/components/HomeSeerSection.tsx">
                  {comparison.homeseer ?
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto" data-id="880h4usbw" data-path="src/components/HomeSeerSection.tsx" /> :

                <div className="w-6 h-6 bg-gray-200 rounded-full mx-auto" data-id="tc97y17rc" data-path="src/components/HomeSeerSection.tsx" />
                }
                </div>
                <div className="text-center" data-id="whnmj3noc" data-path="src/components/HomeSeerSection.tsx">
                  {comparison.others ?
                <CheckCircle className="w-6 h-6 text-green-500 mx-auto" data-id="e3wwp4qm1" data-path="src/components/HomeSeerSection.tsx" /> :

                <div className="w-6 h-6 bg-gray-200 rounded-full mx-auto" data-id="0hie3grrg" data-path="src/components/HomeSeerSection.tsx" />
                }
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust Section */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 lg:p-12 text-white" data-id="2u3abi8hf" data-path="src/components/HomeSeerSection.tsx">
          <div className="grid lg:grid-cols-2 gap-8 items-center" data-id="e37lalyl7" data-path="src/components/HomeSeerSection.tsx">
            <div data-id="p6urizs04" data-path="src/components/HomeSeerSection.tsx">
              <h3 className="text-3xl font-bold mb-4" data-id="1ai1cfscz" data-path="src/components/HomeSeerSection.tsx">
                Confiabilidad Comprobada
              </h3>
              <p className="text-lg opacity-90 mb-6" data-id="wyk6aia52" data-path="src/components/HomeSeerSection.tsx">
                Con 25 años de experiencia en el desarrollo de soluciones de automatización del hogar, 
                HomeSeer ha establecido el estándar de la industria para la confiabilidad, flexibilidad y control local.
              </p>
              <div className="flex items-center space-x-4 mb-6" data-id="2c9n60u1w" data-path="src/components/HomeSeerSection.tsx">
                <Heart className="w-6 h-6 text-red-400" data-id="9rb1v56x1" data-path="src/components/HomeSeerSection.tsx" />
                <span className="text-sm" data-id="xqcge0o6h" data-path="src/components/HomeSeerSection.tsx">Utilizado por profesionales y entusiastas en todo el mundo</span>
              </div>
            </div>
            
            <div className="text-center" data-id="d6089f2ny" data-path="src/components/HomeSeerSection.tsx">
              <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm" data-id="wbbixcwyx" data-path="src/components/HomeSeerSection.tsx">
                <div className="text-4xl font-bold mb-2" data-id="wvhf71vyf" data-path="src/components/HomeSeerSection.tsx">500,000+</div>
                <div className="text-sm opacity-75 mb-4" data-id="mqo6m0hwd" data-path="src/components/HomeSeerSection.tsx">Dispositivos Conectados Exitosamente</div>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white" data-id="sxpx7saed" data-path="src/components/HomeSeerSection.tsx">

                  Descubre HomeSeer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HomeSeerSection;